import React from "react";
import moment from "moment";
import momentTimezone from "moment-timezone";
import qs from "qs";
import { Link } from "react-router";
import { Checkbox, Dialog, FlatButton } from "material-ui";

import config from "../config";

import InlineNoticeLabel from "./partials/inline-notice-label.jsx";
import LinkLike from "./partials/link-like.jsx";
import InjectedSelect from "./partials/injected-select.jsx";
import DatePicker from "./partials/datepicker.jsx";
import WorkingIndicator from "./partials/working-indicator.jsx";
import Label from "./partials/label.jsx";
import SelectField from "./partials/select-field.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import UserActions from "../actions/UserActions";
import UserStore from "../stores/UserStore";
import EventBucketStore from "../stores/EventBucketStore";

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const REPORT_OPTIONS = {
  Select: (
    options,
    intermediateValue,
    value,
    onChange,
    onChangeIntermediate
  ) => {
    return (
      <SelectField
        label={options.label}
        placeholder={"Select an option"}
        value={value}
        autosize={true}
        clearable={false}
        options={options.options}
        onChange={option => onChange(option ? option.value : undefined)}
      />
    );
  },
  Boolean: (
    options,
    intermediateValue,
    value,
    onChange,
    onChangeIntermediate
  ) => {
    return (
      <div style={{ marginTop: "1em" }}>
        <Label>{options.label}</Label>
        <Checkbox
          label={options.checkbox_label}
          checked={!!value}
          onCheck={e => onChange(e.target.checked)}
        />
      </div>
    );
  },
  Date: (options, intermediateValue, value, onChange, onChangeIntermediate) => {
    return (
      <DatePicker
        value={(value + (options.endOfDay ? -ONE_DAY_IN_MS : 0)).toString()}
        label={options.label}
        returnFormat={"x"} // unix timestamp (ms)
        onChange={v => onChange(options.endOfDay ? +v + ONE_DAY_IN_MS : +v)}
      />
    );
  },
  EventBucket: (
    options,
    intermediateValue,
    value,
    onChange,
    onChangeIntermediate
  ) => {
    return (
      <InjectedSelect
        value={value}
        label={options.label}
        labelFormatter={e => e.name}
        type="event_buckets"
        onChange={onChange}
        {...options.props}
      />
    );
  },
  Event: (
    options,
    intermediateValue,
    value,
    onChange,
    onChangeIntermediate
  ) => {
    return (
      <div>
        <InjectedSelect
          value={intermediateValue}
          label={"Event bucket"}
          labelFormatter={e => e.name}
          type="event_buckets"
          onChange={onChangeIntermediate}
          {...options.props}
        />
        {intermediateValue ? (
          <InjectedSelect
            value={value}
            label={options.label}
            labelFormatter={e => e.name}
            type="events"
            eventBucketId={intermediateValue}
            onChange={onChange}
            {...options.props}
          />
        ) : (
          <div>
            <Label>Event</Label>
            <div>
              <i>Select an event bucket above</i>
            </div>
          </div>
        )}
      </div>
    );
  }
};

class Reports extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      reportToShowModal: null,
      processingReport: false,
      showTemporaryExportSuccess: false,
      completedExport: null,
      selectedOptions: {},
      showLegacyReports: false,
      currentMoment: momentTimezone(),
    };

    this._url = this._url.bind(this);
    this.openModal = this.openModal.bind(this);
    this.cancelModal = this.cancelModal.bind(this);
    this.submitModal = this.submitModal.bind(this);
    this._checkTemporaryExport = this._checkTemporaryExport.bind(this);
  }

  static getStores() {
    return [UserStore, EventBucketStore];
  }

  static getPropsFromStores() {
    return UserStore.getState();
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  getStyles() {
    return {
      subhead: {
        marginTop: "1em"
      },
      innerDialog: {
        maxWidth: 250,
        margin: "0 auto"
      }
    };
  }

  _url(path, query) {
    // Add auth to query
    if (!query) {
      query = {};
    }
    query.api_key = this.props.apiKey;
    if (UserStore.isGodInAnyForm()) {
      query.god_become_user = UserStore.getGodCredentials().activeUser;
    }

    return `${config.endpoint}${path}${path.indexOf("?") === -1 ? "?" : "&"
      }${qs.stringify(query)}`;
  }

  openModal(r) {
    let defaults = {};

    Object.entries(r.query).forEach(([k, v]) => {
      if (v.default) {
        defaults[k] = v.default;
      }
    });

    this.setState({
      reportToShowModal: r,
      selectedOptions: defaults
    });
  }

  cancelModal() {
    this.setState({
      reportToShowModal: null,
      showTemporaryExportSuccess: false,
      completedExport: null
    });
  }

  _checkTemporaryExport(id, wait) {
    setTimeout(() => {
      fetch(this._url(`temporary_exports/${id}`))
        .then(r => r.json())
        .then(e => {
          if (e.completed_at) {
            this.setState({ completedExport: e });
          } else {
            this._checkTemporaryExport(id, Math.min(4000, wait * 2)); // backoff: 500ms, 1s, 2s, 4s max wait
          }
        });
    }, wait);
  }

  submitModal() {
    let r = this.state.reportToShowModal,
      query = Object.assign({}, r.query);

    // Inject selected options
    Object.keys(query).forEach(k => {
      if (query[k] && query[k].type) {
        const intermediateKey = query[k].intermediate_value_key;
        query[k] = this.state.selectedOptions[k];
        if (intermediateKey) {
          query[intermediateKey] = this.state.selectedOptions[intermediateKey];
        }
      }
    });

    let url = this._url(
      r.url,
      Object.assign(
        {},
        r.nestQuery ? { [r.nestQuery]: JSON.stringify(query) } : query,
        { tz: moment.tz.guess() }
      )
    );

    if (r.temporary_export) {
      this.setState({
        processingReport: false,
        showTemporaryExportSuccess: true
      });
      fetch(url)
        .then(r => r.json())
        .then(d => {
          if (d.error || !d.temporary_export) {
            this.setState({
              processingReport: false,
              showTemporaryExportSuccess: true,
              completedExport: {
                error: d.error ? d.error.message : "An unknown error occurred"
              }
            });
          } else {
            this.setState({
              processingReport: false,
              showTemporaryExportSuccess: true
            });
            this._checkTemporaryExport(d.temporary_export, 500);
          }
        });
    } else {
      window.open(url);
    }
  }

  componentDidMount() {
    UserActions.fetch();
  }

  componentWillUnmount() {
    clearInterval(this._checkInterval);
  }

  render() {
    let styles = this.getStyles();

    let oneMonthAgo = +moment().startOf("day").subtract(1, "month").format("x");
    let oneWeekAgo = +moment().startOf("day").subtract(1, "week").format("x");
    let tomorrow = +moment().startOf("day").add(1, "day").format("x");

    const RECONCILIATION_QUERY = {
      start: {
        type: "Date",
        label: "Start date",
        default: oneWeekAgo
      },
      end: {
        type: "Date",
        endOfDay: true,
        label: "End date",
        default: tomorrow
      },
      tz_for_output: {
        type: "Select",
        label: "Timezone",
        options:
          momentTimezone.tz.names().map(n => {
            return {
              label: `${n} (${this.state.currentMoment
                .tz(n)
                .format("LT")})`,
              value: n
            };
          }),
        default: "GMT-0"
      },
      group_by: {
        type: "Select",
        label: "Group by",
        options: [
          {
            label: "Individual transaction",
            value: "none"
          },
          {
            label: "Category (columns by category)",
            value: "category"
          },
          {
            label: "Hybrid (face price based, columns by smart category)",
            value: "hybrid"
          }
        ],
        default: "none"
      },
      expand_person: {
        type: "Boolean",
        label: "Purchaser details",
        checkbox_label: "Include purchaser name, email, postal code"
      },
      expand_metadata: {
        type: "Boolean",
        label: "Eventive platform metadata",
        checkbox_label: "Include Eventive system order ID, item ID, etc."
      }
    };

    let reports = [
      {
        title: "Financials",
        visible: true,
        reports: [
          {
            title: "Bank transfer reconciliation by transfer date (unified)",
            url: `reports/ledger/bank_transfer_reconciliation`,
            temporary_export: true,
            description: (
              <div>
                <p>
                  This new ledger-based wizard is in beta and will eventually
                  replace the other bank transfer reconciliation reports. As an
                  early-access tester, please{" "}
                  <a href="mailto:ledgerfeedback@eventive.org">let us know</a>{" "}
                  what report functionality would be most useful to you or if
                  you run into any issues!
                </p>
              </div>
            ),
            query: {
              ...RECONCILIATION_QUERY
            }
          },
          {
            title: "Order reconciliation ledger (unified)",
            url: `reports/ledger/bank_transfer_reconciliation?report_mode=order`,
            temporary_export: true,
            description: (
              <div>
                <p>
                  This new ledger-based wizard is in beta. As an early-access
                  tester, please{" "}
                  <a href="mailto:ledgerfeedback@eventive.org">let us know</a>{" "}
                  what report functionality would be most useful to you or if
                  you run into any issues!
                </p>
              </div>
            ),
            query: {
              ...RECONCILIATION_QUERY
            }
          },
          {
            title: "Sales summary report (gross/net/fees)",
            url: `reports/financials/total_sales_report`,
            temporary_export: true,
            query: {
              event_bucket: {
                type: "EventBucket",
                label: "Event bucket",
                props: { placeholderText: "All event buckets" }
              },
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Sales detail report (gross/net/fees by level/bucket)",
            url: `reports/financials/total_sales_report`,
            temporary_export: true,
            query: {
              detail: true,
              event_bucket: {
                type: "EventBucket",
                label: "Event bucket",
                props: { placeholderText: "All event buckets" }
              },
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Fee reconciliation report (physical events only)",
            url: `reports/financials/fee_reconciliation_report`,
            temporary_export: true,
            query: {
              event_bucket: {
                type: "EventBucket",
                label: "Event bucket",
                props: { placeholderText: "All event buckets" }
              },
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title:
              "Venue sales log (daily net by event date; physical events only)",
            url: `reports/financials/venue_sales_log`,
            temporary_export: true,
            query: {
              event_bucket: { type: "EventBucket", label: "Event bucket" }
            }
          },
          {
            title: "Daily cash log",
            url: `reports/financials/cash_log`,
            query: {
              event_bucket: {
                type: "EventBucket",
                label: "Event bucket",
                props: { placeholderText: "All event buckets" }
              }
            }
          },
          {
            title: "Daily sales by box office account",
            url: `reports/financials/box_office_account_log`,
            query: {
              event_bucket: {
                type: "EventBucket",
                label: "Event bucket",
                props: { placeholderText: "All event buckets" }
              }
            }
          },
          {
            title: "Box office shift report  (physical events only)",
            url: `reports/financials/shift_report`,
            temporary_export: true,
            query: {
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Donations grouped by associated event",
            url: `reports/financials/total_sales_report`,
            temporary_export: true,
            query: {
              detail: true,
              donations_by_event: true,
              event_bucket: {
                type: "EventBucket",
                label: "Event bucket",
                props: { placeholderText: "All event buckets" }
              },
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "All orders",
            url: `orders.csv?temporary_export=true`,
            nestQuery: "conditions",
            temporary_export: true,
            query: {
              event_bucket: {
                type: "EventBucket",
                label: "Event bucket",
                props: { placeholderText: "All event buckets" }
              },
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          }
        ]
      },
      {
        title: "Attendees",
        visible: true,
        reports: [
          {
            title: "Single ticket buyers who are not passholders",
            url: `reports/single_ticket_buyers_not_passholders`,
            query: {
              event_bucket: { type: "EventBucket", label: "Event bucket" }
            }
          },
          {
            title: "Tickets by person (will call)",
            url: `reports/tickets_by_person`,
            query: {
              event_bucket: { type: "EventBucket", label: "Event bucket" }
            }
          },
          {
            title: "Attendees ranked by number of events attended",
            url: `reports/attendees_ranked`,
            query: {
              event_bucket: { type: "EventBucket", label: "Event bucket" }
            }
          },
          {
            title: "Person notes report",
            url: `reports/person_notes`,
            query: {}
          }
        ]
      },
      {
        title: "Miscellaneous",
        visible: true,
        reports: [
          {
            title: "Distributor splits (with passes)",
            url: `reports/financials/distributor_splits`,
            temporary_export: true,
            query: {
              event_bucket: { type: "EventBucket", label: "Event bucket" }
            }
          },
          {
            title: "Streaming analytics report",
            url: `reports/streams`,
            temporary_export: true,
            query: {
              event_bucket: { type: "EventBucket", label: "Event bucket" },
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Mailing list opt-in by event bucket or event",
            url: `reports/mailing_opt_ins`,
            temporary_export: true,
            query: {
              event: {
                type: "Event",
                intermediate_value_key: "event_bucket",
                label: "Event (optional)"
              }
            }
          },
          {
            show:
              UserStore.isGod() ||
              [
                "5e34ae2cc39cec003138f598", // AARP
                "5eb9ae8bbd869b0071708e19" // IDA
              ].indexOf((UserStore.getUser() || {}).id) !== -1,
            title: "Virtual screening user supplementary data",
            badge: "Limited Beta",
            url: `reports/user_person_supplementary_data`,
            temporary_export: true,
            query: {
              event: {
                type: "Event",
                intermediate_value_key: "event_bucket",
                label: "Event (optional)"
              }
            }
          }
        ].filter(r => typeof r.show === "undefined" || r.show)
      },
    ];

    let legacyReports = [
      {
        title: "Legacy Reports",
        visible: true,
        reports: [
          {
            title: "Bank transfer log",
            url: `reports/financials/transfer_log`,
            temporary_export: true,
            query: {
              format: "csv",
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Bank transfer log (+ gross sales/fees)",
            url: `reports/financials/transfer_log`,
            temporary_export: true,
            query: {
              gross_and_fees: true,
              format: "csv",
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Detailed bank transfer log (by level/bucket)",
            url: `reports/financials/transfer_log`,
            temporary_export: true,
            query: {
              detail: true,
              format: "csv",
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Bank transfer reconciliation by screening",
            url: `reports/financials/transfer_reconciliation`,
            temporary_export: true,
            query: {
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          {
            title: "Bank transfer reconciliation by individual purchase",
            url: `reports/financials/transfer_reconciliation`,
            temporary_export: true,
            query: {
              by_individual_purchase: true,
              start: {
                type: "Date",
                label: "Start date",
                default: oneMonthAgo
              },
              end: {
                type: "Date",
                endOfDay: true,
                label: "End date",
                default: tomorrow
              }
            }
          },
          /*{
            title: 'Detailed bank transfer log (by event/pass/etc.)',
            url: `reports/financials/transfer_log`,
            query: {
              detailed: true,
              format: 'csv',
              start: { type: 'Date', label: 'Start date', default: oneMonthAgo },
              end: { type: 'Date', endOfDay: true, label: 'End date', default: tomorrow }
            }
          },*/
        ]
      }
    ];

    return (
      <div>
        <Dialog
          title={
            this.state.reportToShowModal && this.state.reportToShowModal.title
          }
          actions={[
            <FlatButton
              label={this.state.showTemporaryExportSuccess ? "Close" : "Cancel"}
              onTouchTap={this.cancelModal}
            />
          ].concat(
            !this.state.showTemporaryExportSuccess
              ? [
                <FlatButton
                  label={
                    (this.state.reportToShowModal &&
                      this.state.reportToShowModal.generate_label) ||
                    "Generate report"
                  }
                  onTouchTap={this.submitModal}
                  primary={true}
                />
              ]
              : []
          )}
          autoDetectWindowHeight={false}
          autoScrollBodyContent={false}
          bodyStyle={{ overflowY: "visible" }}
          open={!!this.state.reportToShowModal}
        >
          <div>
            {this.state.reportToShowModal ? (
              this.state.processingReport ? (
                <WorkingIndicator centered={true} />
              ) : this.state.showTemporaryExportSuccess ? (
                this.state.completedExport ? (
                  this.state.completedExport.error ? (
                    <div>
                      <p>
                        Unfortunately, we ran into an error preparing your
                        report. Please try again, or contact us if the problem
                        persists.
                      </p>
                      <p>
                        <i>
                          Error:{" "}
                          <strong>{this.state.completedExport.error}</strong>
                        </i>
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p>Great news, your report is ready!</p>
                      <p>
                        <strong>
                          {this.state.completedExport.description}
                        </strong>
                        <br />
                        <a
                          target="_blank"
                          href={this.state.completedExport.download_url}
                        >
                          <strong>Click here to download.</strong>
                        </a>
                      </p>
                    </div>
                  )
                ) : (
                  <div>
                    <WorkingIndicator centered={true} />
                    <p>
                      Your report is now being generated! This may take anywhere
                      from a few seconds to a few minutes. Keep this page open
                      and it will automatically update when your report is
                      ready.
                    </p>
                    <p>
                      Feel free to close this page. You will receive an email
                      with a download link as soon as your report has been
                      generated.
                    </p>
                  </div>
                )
              ) : (
                (() => {
                  const options = Object.entries(
                    this.state.reportToShowModal.query
                  ).filter(([k, v]) => v && v.type);

                  if (!options.length) {
                    return (
                      <div>
                        There are no available options to configure for this
                        report.
                      </div>
                    );
                  }

                  return (
                    <div>
                      {this.state.reportToShowModal.description ? (
                        <div style={{ marginTop: "-1em" }}>
                          {this.state.reportToShowModal.description}
                        </div>
                      ) : null}
                      <div style={styles.innerDialog}>
                        {options.map(([key, options]) => {
                          return REPORT_OPTIONS[options.type](
                            options,
                            this.state.selectedOptions[
                              options.intermediate_value_key ||
                                `${key}--intermediate`
                            ],
                            this.state.selectedOptions[key],
                            value => {
                              let selected = Object.assign(
                                {},
                                this.state.selectedOptions
                              );
                              selected[key] = value;
                              this.setState({ selectedOptions: selected });
                            },
                            intermediateValue => {
                              let selected = Object.assign(
                                {},
                                this.state.selectedOptions
                              );
                              selected[
                                options.intermediate_value_key ||
                                  `${key}--intermediate`
                              ] = intermediateValue;
                              this.setState({ selectedOptions: selected });
                            }
                          );
                        })}
                      </div>
                    </div>
                  );
                })()
              )
            ) : null}
          </div>
        </Dialog>
        <h1>Reports</h1>
        <p>
          Export reports for further analysis (available in CSV / XLSX format).
        </p>

        {reports
          .filter(r => r.visible)
          .map((r, i) => (
            <div key={i}>
              <h2 style={styles.subhead}>{r.title}</h2>
              {r.reports.map((r, i) => (
                <li key={i}>
                  {r.badge ? (
                    <InlineNoticeLabel
                      style={{ marginRight: 5, fontWeight: "bold" }}
                      label={r.badge}
                    />
                  ) : null}
                  {r.title} &ndash;{" "}
                  <LinkLike onTouchTap={() => this.openModal(r)}>
                    {r.generate_label || "generate report"}
                  </LinkLike>
                </li>
              ))}
            </div>
          ))}

        <div>
          <h2 style={styles.subhead}>Ledger Beta Reports</h2>
          <li>
            <InlineNoticeLabel
              style={{ marginRight: 5, fontWeight: "bold" }}
              label="Beta"
            />
            Custom ledger report &ndash;{" "}
            <Link to="/analytics/ledger">build report</Link>
          </li>
          <li>
            <InlineNoticeLabel
              style={{ marginRight: 5, fontWeight: "bold" }}
              label="Beta"
            />
            Scheduled ledger reports &ndash;{" "}
            <Link to="/scheduled_reports">schedule reports</Link>
          </li>
          {/* {UserStore.isGod() ? (
            <li>
              <InlineNoticeLabel
                style={{ marginRight: 5, fontWeight: "bold" }}
                label="God beta"
              />
              Box Office Report &ndash;{" "}
              <Link to="/analytics/box_office_report">generate report</Link>
            </li>
          ) : null} */}
        </div>

        <h2 style={styles.subhead}>Cross-Event-Bucket Comparison</h2>
        <li>
          Daily total sales comparison between event buckets &ndash;{" "}
          <Link to="reports/cross_event_bucket">generate graphs</Link>
        </li>

        {legacyReports
          .filter(r => r.visible)
          .map((r, i) => (
            <div key={i}>
              <div
                style={{ cursor: "pointer" }}
                onClick={() => { this.setState({ showLegacyReports: !this.state.showLegacyReports }) }}>
                  <h2 style={styles.subhead}>{this.state.showLegacyReports ? "▾" : "▸"}{" "}{r.title}</h2>
              </div>
              {this.state.showLegacyReports ?
                r.reports.map((r, i) => (
                  <li key={i}>
                    {r.badge ? (
                      <InlineNoticeLabel
                        style={{ marginRight: 5, fontWeight: "bold" }}
                        label={r.badge}
                      />
                    ) : null}
                    {r.title} &ndash;{" "}
                    <LinkLike onTouchTap={() => this.openModal(r)}>
                      {r.generate_label || "generate report"}
                    </LinkLike>
                  </li>
                ))
              : null}
            </div>
          ))}

      </div>
    );
  }
}

export default connectToStores(Reports);



// WEBPACK FOOTER //
// ./src/app/components/reports.jsx